<template>
    <div class="Preview">
        <div class="flex row-stretch-top Top">
            <Tracking
                :step="step"
                :length="(fields || []).length"
                class="flex-1 mb-1"
            />
            <div class="Options flex column">
                <div class="flex row-center">
                    <div class="text-body2 text-weight-medium">
                        {{ $t('wizard.preview.homogeinzeAttribute') }}
                    </div>
                    <QToggle
                        v-model="homogenizeAttr"
                        color="primary"
                        keepColor
                        @update:modelValue="handleChangeHomogenizeAttr"
                    />
                </div>
                <div class="flex row-center">
                    <div class="text-body2 text-weight-medium">
                        {{ $t('wizard.preview.numberFormat.label') }}
                    </div>
                    <QSelect
                        ref="numberFormat"
                        v-model="numberFormat"
                        dense
                        outlined
                        behavior="menu"
                        class="Select"
                        :class="{ 'Select--warning': showNumberFormatWarning }"
                        :options="numberFormats"
                        @update:modelValue="handleChangeNumberFormat"
                    >
                        <template #append>
                            <q-icon
                                v-if="showNumberFormatWarning"
                                name="warning"
                                color="warning"
                                size="18px"
                                class="cursor-pointer"
                            >
                                <q-tooltip
                                    class="bg-white text-warning text-body2"
                                    :style="{ border: '1px solid var(--q-warning)' }"
                                    maxWidth="300px"
                                >
                                    {{ $t('wizard.preview.mismatchedNumberFormat') }}
                                </q-tooltip>
                            </q-icon>
                        </template>
                        <template #option="scope">
                            <QItem v-bind="scope.itemProps" v-on="scope.itemEvents || {}">
                                <QItemSection>
                                    <QItemLabel>{{ scope.opt.label }}</QItemLabel>
                                    <QItemLabel caption>
                                        {{ scope.opt.description }}
                                    </QItemLabel>
                                </QItemSection>
                            </QItem>
                        </template>
                    </QSelect>
                </div>
            </div>
        </div>
        <QTable
            bordered
            flat
            :columns="normalizeColumns"
            :rows="rows"
            :loading="loading"
            :pagination="pagination"
            hideBottom
            rowKey="index"
            separator="cell"
            class="table-list"
        >
            <template #header="props">
                <QTr
                    v-if="selectable"
                    :props="props"
                    class="FakeRow"
                >
                    <QTh
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                    >
                        <div
                            v-if="col.checked || col.hovered"
                            class="Field flex row-stretch-center"
                            :class="{ optional: (fields[col.checkedIndex] || fields[col.hoveredIndex] || {}).optional }"
                        >
                            <div class="flex row-center q-gutter-xs">
                                <span
                                    class="text-body2 text-weight-medium"
                                >
                                    {{ (fields[col.checkedIndex] || fields[col.hoveredIndex] || {}).label }}
                                </span>
                                <q-icon
                                    v-if="((fields[col.checkedIndex]
                                        || fields[col.hoveredIndex] || {}).label === $t('wizard.activity'))
                                        && !col.selectableAsActivity"
                                    name="warning"
                                    color="warning"
                                    size="18px"
                                    class="cursor-pointer"
                                >
                                    <q-tooltip
                                        class="bg-white text-warning text-body2"
                                        :style="{ border: '1px solid var(--q-warning)' }"
                                        maxWidth="300px"
                                    >
                                        {{ $t('wizard.activityWarning') }}
                                    </q-tooltip>
                                </q-icon>
                            </div>
                            <q-icon
                                size="18px"
                                name="close"
                                clickable
                                style="cursor: pointer;"
                                @click="handleClickRow({ col }, false)"
                            />
                        </div>
                    </QTh>
                </QTr>
                <QTr :props="props">
                    <QTh
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="Header"
                        :class="{
                            hovered: hovered === col.field,
                            cursor: col.checked,
                            disabled: !col.checked && disabledChecked,
                        }"
                        @mouseover="handleRowMouseOver({ col }, $event)"
                        @focus="handleRowMouseOver({ col }, $event)"
                        @mouseleave="handleRowMouseLeave({ col }, $event)"
                        @blur="handleRowMouseLeave({ col }, $event)"
                        @click="handleClickRow({ col }, $event)"
                    >
                        <div class="flex column items-start">
                            <span>
                                {{ col.label }}
                            </span>
                        </div>
                    </QTh>
                </QTr>
                <QTr :props="props">
                    <QTh
                        v-for="(col, index) in props.cols"
                        :key="col.name"
                        :props="props"
                        class="Header"
                        :class="{
                            hovered: hovered === col.field,
                            cursor: col.checked,
                            disabled: !col.checked && disabledChecked,
                        }"
                    >
                        <div class="flex column items-start">
                            <QSelect
                                :modelValue="getModifiedType(index)"
                                :disable="col.disabledCol || (col.checked && fields[col.checkedIndex].key === fieldKeys.TRACE)"
                                lazyRules
                                dense
                                outlined
                                behavior="menu"
                                style="width: 100%;"
                                :options="typeOptions(col.type)"
                                @update:modelValue="(val) => handleTypeChange(index, val)"
                            />
                        </div>
                    </QTh>
                </QTr>
            </template>
            <template #body-cell="props">
                <QTd
                    :props="props"
                    class="Item"
                    :class="{
                        hovered: hovered === props.col.field,
                        cursor: props.col.checked,
                        disabled: !props.col.checked && disabledChecked,
                    }"
                    @mouseover="handleRowMouseOver(props, $event)"
                    @focus="handleRowMouseOver(props, $event)"
                    @mouseleave="handleRowMouseLeave(props, $event)"
                    @blur="handleRowMouseLeave(props, $event)"
                    @click="handleClickRow(props, $event)"
                >
                    {{ props.value }}
                </QTd>
            </template>
            <template #bottom>
                <QTr>
                    <QTh />
                </QTr>
            </template>
        </QTable>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import Tracking from './Tracking.vue'

const FIELD_KEYS = {
    TRACE: 'traceId',
    ACTIVITY: 'activity',
    START: 'start',
    END: 'end',
}

const LIMIT = 4

export default {
    name: 'PreviewPanel',
    components: { Tracking },
    inject: ['App'],
    props: {
        modelValue: VueTypes.any,
        columns: VueTypes.any,
        rows: VueTypes.any,
        rawColumns: VueTypes.any,
        loading: VueTypes.bool.def(false),
        selectable: VueTypes.bool.def(true),
        initialNumberFormat: VueTypes.object,
        numberFormats: VueTypes.array,
    },
    emits: ['update:modelValue', 'update:formatNumber'],
    data() {
        const { user } = this.App
        return {
            hovered: undefined,
            modifiedColumns: [],
            checkeds: [],
            fieldKeys: FIELD_KEYS,
            thousandCharacter: '',
            decimalCharacter: '',
            homogenizeAttr: false,
            user: { ...user },
            numberFormat: undefined,
            pagination: {
                page: 0,
                rowsPerPage: 0,
            },
        }
    },
    computed: {
        disabledChecked() {
            return this.checkeds.filter((col) => !!col).length >= LIMIT
        },
        normalizeColumns() {
            return (this.columns || []).map((col) => {
                const { field } = col
                const hovered = this.hovered === field && !this.disabledChecked
                const hoveredIndex = hovered ? this.indexEmpty() : undefined
                const checked = this.isChecked(field)
                const checkedIndex = checked ? this.indexChecked(field) : undefined
                return {
                    ...col,
                    hovered,
                    hoveredIndex,
                    checked,
                    checkedIndex,
                }
            })
        },
        fields() {
            return [
                {
                    key: FIELD_KEYS.TRACE,
                    label: this.$t('wizard.trace'),
                    tooltip: this.$t('wizard.preview.tooltips.TRACE'),
                },
                {
                    key: FIELD_KEYS.ACTIVITY,
                    label: this.$t('wizard.activity'),
                    tooltip: this.$t('wizard.preview.tooltips.ACTIVITY'),
                },
                {
                    key: FIELD_KEYS.START,
                    label: this.$t('wizard.startTime'),
                    tooltip: this.$t('wizard.preview.tooltips.START'),
                },
                {
                    key: FIELD_KEYS.END,
                    label: this.$t('wizard.endTime'),
                    optional: true,
                    tooltip: this.$t('wizard.preview.tooltips.END'),
                },
            ]
        },
        step() {
            const numberOfSteps = this.checkeds.filter(Boolean).length
            return !this.disabledChecked
                ? {
                    num: numberOfSteps + 1,
                    ...this.fields[this.indexEmpty()],
                }
                : undefined
        },
        showNumberFormatWarning() {
            const { locationFormat } = this.user
            return this.numberFormat.value !== locationFormat
        },
    },
    watch: {
        columns: {
            handler(cols) {
                // Keep modifiedColumns aligned with columns length.
                this.modifiedColumns = (cols || []).map((c) => ({ ...c, name: c.name, type: c.type }))
            },
            immediate: true,
        },
    },
    created() {
        this.numberFormat = this.initialNumberFormat
    },
    mounted() {
        this.initialize()
    },
    methods: {
        getModifiedType(index) {
            return this.modifiedColumns?.[index]?.type
        },
        handleTypeChange(index, type) {
            if (!this.modifiedColumns?.[index]) {
                this.modifiedColumns = (this.columns || []).map((c) => ({ ...c, name: c.name, type: c.type }))
            }
            if (!this.modifiedColumns?.[index]) return
            this.modifiedColumns[index].type = type
            this.updateColumnTypes()
        },
        initialize() {
            this.checkeds = this.fields.map((item) => (this.modelValue[item.key] || {}).value || undefined)
            const newValue = {
                ...(this.modelValue || {}),
                modifiedTypes: this.rawColumns
                    .map((c) => ({
                        ...c,
                        type: this.modifiedColumns.find((t) => t.name === c.name)?.type,
                        relevant: false,
                    })),
            }
            this.$emit('update:modelValue', newValue)
        },
        isChecked(field) {
            return this.checkeds.includes(field)
        },
        indexChecked(field) {
            return this.checkeds.findIndex((f) => f === field)
        },
        indexEmpty() {
            return this.checkeds.findIndex((f) => f === undefined)
        },
        getSlotOfField(field) {
            const currentSlot = this.indexChecked(field)
            return currentSlot !== -1 ? currentSlot : this.indexEmpty()
        },
        handleRowMouseOver({ col }) {
            this.hovered = col.field
        },
        handleRowMouseLeave() {
            this.hovered = undefined
        },
        handleClickRow({ col }) {
            const checked = this.isChecked(col.field)
            if (!this.disabledChecked) this.handleClickCol(col, !checked)
            else if (checked) this.handleClickCol(col, false)
        },
        handleClickCol(col, checked) {
            const { field, label } = col
            const slot = this.getSlotOfField(field)
            const fieldKey = (this.fields[slot] || {}).key
            const value = {
                fieldKey, field, value: field, label, slot, checked,
            }
            if (fieldKey === this.fieldKeys.TRACE && checked) {
                const columnIndex = this.modifiedColumns.findIndex((c) => c.name === col.name)
                this.modifiedColumns[columnIndex].type = 'STRING'
            }
            this.handleInput(value)
        },
        updateColumnTypes() {
            const newValue = {
                ...(this.modelValue || {}),
                modifiedTypes: this.modifiedColumns.map((c) => ({
                    ...c,
                    relevant: false,
                })),
            }
            this.$emit('update:modelValue', newValue)
        },
        typeOptions(type) {
            const options = [type]
            if (type !== 'STRING') options.push('STRING')
            return options
        },
        handleChangeNumberFormat({ value: numberFormat }) {
            this.$emit('update:formatNumber', numberFormat)
        },
        handleChangeHomogenizeAttr() {
            const newValue = {
                ...(this.modelValue || {}),
                homogenizeAttr: this.homogenizeAttr,
            }
            this.$emit('update:modelValue', newValue)
        },
        handleInput({
            fieldKey, field, value, label, slot, checked,
        } = {}) {
            const checkeds = [...this.checkeds.slice(0, slot), checked ? field : undefined, ...this.checkeds.slice(slot + 1)]
            const newValue = {
                ...(this.modelValue || {}),
                [fieldKey]: checked ? { label, value } : undefined,
                modifiedTypes: this.rawColumns
                    .map((c) => ({
                        ...c,
                        type: this.modifiedColumns.find((t) => t.name === c.name)?.type,
                        relevant: false,
                    })),
            }
            this.checkeds = checkeds
            this.$emit('update:modelValue', newValue)
        },
    },
}
</script>
<style scoped lang="scss">
.Preview{
    height:100%;
}
.Top {
    height:fit-content;
    margin-bottom: 10px;
}

.Options {
    width: 35%;
    margin-right: 5px;
}

.table-list {
    max-height: 51vh;
    @media screen and (min-height: $md) {
        max-height: 54vh;
    }
  padding-bottom:20px;
  &:deep() {
    flex: 1;
    color: $table-color !important;
    background: transparent !important;

    .q-table {
        tbody {
            td {
                padding: 15px;
                min-width: 130px;

                &:before {
                    background: transparent;
                }
            }
        }
    }
}

  .q-table__top,
  thead tr th {
    color: $table-head-color;
    font-size: $table-head-size;
    background: $table-head-bg;
}

  thead tr th {
    position: sticky;
    z-index: 1;
}
  thead tr:first-child th{
    top: 0;
  }
  thead tr:nth-child(2) th{
    top: 48px;
  }
    thead tr:nth-child(3) th{
        top: 96px;
    }
  &.q-table--loading thead tr:last-child th {
        top: 96px;
}

  tbody{
    scroll-margin-top: 48px
}
}

.Header {
    &.disabled {
        opacity:1 !important;
    }
    &.hovered:not(.disabled) {
        cursor: pointer;

        &:deep(.q-checkbox__bg) {
            border-color: $white;
        }
    }
}

.FakeRow {
    background: $table-head-fake-bg;

    th {
        padding: 0 2px;
        border-left: 0;
        border-right: 0;
        background: $table-head-fake-bg !important;
    }
}

.Item {
    &.cursor {
        cursor: pointer
    }
    &.disabled {
        cursor: not-allowed;
    }
    &.hovered:not(.disabled) {
        background: rgba($primary, 0.05);
        cursor: pointer
    }
}

.Checkbox {
    &:deep(.q-checkbox__inner) {
        display: none;
    }
}

.Field {
    padding: 8px;
    border: 1px solid $primary;
    border-radius: $border-radius;
    color: white;
    background-color: $primary;

    &.optional {
        background-color: $secondary;
        border-color: $secondary;
    }
}
.InputCharacter {
    width: 50px;
    margin-left: 10px;
}

.Select {
    overflow: none;
    margin-left: 10px;
}

.Select--warning {
    &:deep(.q-field__control:before) {
        border-color: var(--q-warning) !important;
    }

    &:deep(.q-field__control:after) {
        border-color: var(--q-warning) !important;
    }
}
 .Alert {
    color: #ffec54;
 }
</style>
